/**
 * AddUserModal Component
 * Modal for creating new users with role selection
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { USER_ROLES } from '@/constants/beStudyMockData';
import { Loader2, Shield, User, ClipboardCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Role option card
 */
function RoleOption({ role, isSelected, onSelect }) {
  const roleConfig = {
    [USER_ROLES.ADMIN]: {
      icon: Shield,
      title: 'Admin',
      description: 'Full access to all features including user and template management',
      color: 'text-red-500',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
    },
    [USER_ROLES.USER]: {
      icon: User,
      title: 'User',
      description: 'Can create and edit documents, submit for review',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    [USER_ROLES.REVIEWER]: {
      icon: ClipboardCheck,
      title: 'Reviewer',
      description: 'Can review documents, add comments, approve or reject',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
    },
  };

  const config = roleConfig[role];
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(role)}
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left w-full',
        isSelected
          ? `${config.borderColor} ${config.bgColor}`
          : 'border-border hover:border-muted-foreground/50'
      )}
      data-testid={`role-option-${role.toLowerCase()}`}
    >
      <div className={cn('p-2 rounded-lg', isSelected ? config.bgColor : 'bg-muted')}>
        <Icon className={cn('h-5 w-5', isSelected ? config.color : 'text-muted-foreground')} />
      </div>
      <div>
        <h4 className={cn('font-medium', isSelected && config.color)}>{config.title}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">{config.description}</p>
      </div>
    </button>
  );
}

/**
 * AddUserModal component
 */
export function AddUserModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  existingEmails = [],
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (existingEmails.some((e) => e.toLowerCase() === formData.email.toLowerCase())) {
      newErrors.email = 'A user with this email already exists';
    }

    if (!formData.role) {
      newErrors.role = 'Please select a role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit?.({
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      role: formData.role,
      department: formData.department.trim() || null,
    });

    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      role: '',
      department: '',
    });
    setErrors({});
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="add-user-modal">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>
            Create a new user account with specific role and permissions
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="user-name">Full Name *</Label>
              <Input
                id="user-name"
                placeholder="e.g., Dr. John Smith"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
                data-testid="user-name-input"
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="user-email">Email Address *</Label>
              <Input
                id="user-email"
                type="email"
                placeholder="e.g., john.smith@company.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
                data-testid="user-email-input"
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Department */}
            <div className="space-y-2">
              <Label htmlFor="user-department">Department</Label>
              <Input
                id="user-department"
                placeholder="e.g., Regulatory Affairs"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                data-testid="user-department-input"
              />
            </div>

            {/* Role Selection */}
            <div className="space-y-3">
              <Label>User Role *</Label>
              <div className="space-y-2">
                {Object.values(USER_ROLES).map((role) => (
                  <RoleOption
                    key={role}
                    role={role}
                    isSelected={formData.role === role}
                    onSelect={(r) => handleChange('role', r)}
                  />
                ))}
              </div>
              {errors.role && (
                <p className="text-xs text-red-500">{errors.role}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="create-user-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddUserModal;
